﻿import { SvgIcon } from '@progress/kendo-react-common';
import { ReactNode, useEffect } from 'react';
import Coordinate from '../../../../../common/coordinate';
import { DateTimeFormatter, UnitFormatter } from '../../../../../formatters';
import { AirplaneOutline, AirportTowerOutline, ArrowRightSolid, ClockOutline, LocationPinOutline } from '../../../../../icons/icons';
import { AirportWithDistance } from '../../../../../static/static-airports';
import { statusToReadableString } from '../../../../components/FlightStatusBadge';
import { LoadingIndicatorCircular } from '../../../../components/indicators/LoadingIndicatorCircular';
import { toReadableHeading } from '../../../../utils/bearing';
import { CityLocationDetails, FlightCurrentPosition, LastKnownLocationInfo, useFlightPosition } from '../../hooks/use-flight-position';

interface PositionDetailsProps {
    acid?: string;
    onGoToCoordinates?: (coordinates: Coordinate) => void;
    onOpenAirportDetails?: (id: string) => void;
}

export default function PositionDetails({ acid, onGoToCoordinates, onOpenAirportDetails }: PositionDetailsProps) {
    const { currentPosition, lastKnownLocation, isLoading, error, fetchFlightPosition, clear } = useFlightPosition();

    useEffect(() => {
        if (acid) {
            fetchFlightPosition(acid);
        } else {
            clear();
        }
    }, [acid, fetchFlightPosition, clear]);

    const placeholderIfEmpty = (input?: string | null, placeholder: string = '-'): string => {
        return !input || input.trim() === '' ? placeholder : input;
    };

    const renderAirportLink = (id: string | null): ReactNode => {
        if (!id || id.trim() === '') {
            return <span>unknown airport</span>;
        }
        return (
            <a className="airport-link cursor-pointer" onClick={() => onOpenAirportDetails?.(id)}>
                {id}
            </a>
        );
    };

    const renderLocationLink = (position: Coordinate): ReactNode => {
        return (
            <a className="airport-link cursor-pointer" onClick={() => onGoToCoordinates?.(position)}>
                ({position.lat},{position.lng})
            </a>
        );
    };

    const renderStatus = (currentPosition: FlightCurrentPosition): ReactNode => {
        if (currentPosition.onGround) {
            const arrivedAt = (currentPosition.distanceToDestination ?? 0) < 15 ? 'destination' : 'origin';
            return (
                <span className="w-75">
                    On ground at {arrivedAt} ({renderAirportLink(currentPosition.origin)}{' '}
                    <SvgIcon size="small" icon={ArrowRightSolid} className="secondary-text-color" />{' '}
                    {renderAirportLink(currentPosition.destination)})
                </span>
            );
        }

        return (
            <span className="w-75">
                {statusToReadableString(currentPosition.status, currentPosition.onGround)} from {renderAirportLink(currentPosition.origin)}{' '}
                to {renderAirportLink(currentPosition.destination)} ({placeholderIfEmpty(currentPosition.originAirport?.name)}/
                {placeholderIfEmpty(currentPosition.destinationAirport?.name)})
            </span>
        );
    };

    const renderCurrentLocation = (currentPosition: FlightCurrentPosition): ReactNode => {
        return (
            <span className="w-75">
                {currentPosition.position.isValid && <>{renderLocationLink(currentPosition.position)} </>}
                {currentPosition.onGround ? (
                    <>
                        At{' '}
                        {(currentPosition.distanceToDestination ?? 0) < 15 ? (
                            <>
                                {renderAirportLink(currentPosition.destination)} (
                                {placeholderIfEmpty(currentPosition.destinationAirport?.name)})
                            </>
                        ) : (
                            <>
                                {renderAirportLink(currentPosition.origin)} ({placeholderIfEmpty(currentPosition.originAirport?.name)})
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {currentPosition.distanceToDestination != null && (
                            <>{UnitFormatter.formatDistance(currentPosition.distanceToDestination)}</>
                        )}
                        {currentPosition.destinationAirport != null ? (
                            <>
                                {' '}
                                {toReadableHeading(
                                    currentPosition.destinationAirport.location?.headingTo(currentPosition.position)
                                )} of {renderAirportLink(currentPosition.destination)} (destination)
                            </>
                        ) : (
                            <>En route to {renderAirportLink(currentPosition.destination)}</>
                        )}
                    </>
                )}
                {getNearestCityString(currentPosition.nearestCity)}
                {renderNearestAirport(currentPosition.nearestAirport)}
            </span>
        );
    };

    const renderLastKnownLocationDetails = (lastKnownLocation: LastKnownLocationInfo): ReactNode => {
        return (
            <span className="w-75">
                {lastKnownLocation.position.isValid && <>{renderLocationLink(lastKnownLocation.position)} </>}
                {lastKnownLocation.destination && lastKnownLocation.destination.trim() !== '' ? (
                    lastKnownLocation.isAtDestination ? (
                        <>
                            At {renderAirportLink(lastKnownLocation.destination)} (
                            {placeholderIfEmpty(lastKnownLocation.destinationAirport?.name, '-')})
                        </>
                    ) : (
                        <>
                            {lastKnownLocation.distanceToDestination != null && (
                                <>{UnitFormatter.formatDistance(lastKnownLocation.distanceToDestination)}</>
                            )}
                            {lastKnownLocation.destinationAirport != null ? (
                                <>
                                    {' '}
                                    {toReadableHeading(
                                        lastKnownLocation.destinationAirport.location?.headingTo(lastKnownLocation.position)
                                    )}{' '}
                                    of {renderAirportLink(lastKnownLocation.destination)} (intended destination)
                                </>
                            ) : (
                                <>En route to {renderAirportLink(lastKnownLocation.destination)}</>
                            )}
                        </>
                    )
                ) : (
                    <>Intended destination is unknown</>
                )}
                {getNearestCityString(lastKnownLocation.nearestCity)}
                {renderNearestAirport(lastKnownLocation.nearestAirport)}
            </span>
        );
    };

    const getNearestCityString = (nearestCity: CityLocationDetails | null): string => {
        if (!nearestCity) {
            return '';
        }
        return `, ${UnitFormatter.formatDistance(nearestCity.distance)} ${toReadableHeading(nearestCity.fromBearing)} of ${nearestCity.name}`;
    };

    const renderNearestAirport = (nearestAirport: AirportWithDistance | null): ReactNode => {
        if (!nearestAirport || (!nearestAirport.icao && !nearestAirport.iata && !nearestAirport.faa)) {
            return null;
        }

        const airportId = nearestAirport.icao
            ? nearestAirport.icao
            : nearestAirport.iata
              ? nearestAirport.iata
              : nearestAirport.faa
                ? nearestAirport.faa
                : null;

        return (
            <span>
                {`, ${UnitFormatter.formatDistance(nearestAirport.distance)} ${toReadableHeading(nearestAirport.bearing)} of `}
                {renderAirportLink(airportId)}
            </span>
        );
    };

    const fetchFailed = error !== null;

    return (
        <div className="pe-auto w-100 max-h-100 filters-card position-component-wrapper" style={{ maxWidth: '500px' }}>
            <div className="p-0 pb-1 overflow-auto">
                {currentPosition && (
                    <>
                        <div className="section-header k-card-header background d-flex align-items-center buttons-tooltip-bottom">
                            <span className="flex-grow-1">Current</span>
                        </div>

                        <div className="d-flex flex-column gap-2 overflow-auto buttons-tooltip-bottom p-2">
                            <div className="d-flex gap-1 w-100 justify-content-start align-items-start buttons-tooltip-bottom">
                                <SvgIcon size="medium" icon={AirplaneOutline} className="flex-shrink-0 secondary-text-color" />
                                <div className="w-100 d-flex">
                                    <span className="secondary-text-color w-25">Status:</span>
                                    {renderStatus(currentPosition)}
                                </div>
                            </div>
                            <div className="d-flex gap-1 w-100 justify-content-start align-items-start buttons-tooltip-bottom">
                                <SvgIcon size="medium" icon={LocationPinOutline} className="flex-shrink-0 secondary-text-color" />
                                <div className="w-100 d-flex">
                                    <span className="secondary-text-color w-25">Location:</span>
                                    {renderCurrentLocation(currentPosition)}
                                </div>
                            </div>
                            <div className="d-flex gap-1 w-100 align-items-start buttons-tooltip-bottom">
                                <SvgIcon size="medium" icon={ClockOutline} className="flex-shrink-0 secondary-text-color" />
                                <div className="w-100 d-flex">
                                    <span className="secondary-text-color w-25">ETA:</span>
                                    <span className="w-75">
                                        {placeholderIfEmpty(
                                            currentPosition.arrivalTime
                                                ? DateTimeFormatter.formatTime(currentPosition.arrivalTime)
                                                : undefined
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {lastKnownLocation && (
                    <>
                        <div className="section-header k-card-header background d-flex align-items-center buttons-tooltip-bottom">
                            <span className="flex-grow-1">Last known location</span>
                        </div>

                        <div className="d-flex flex-column gap-2 overflow-auto buttons-tooltip-bottom p-2">
                            <div className="d-flex gap-1 w-100 justify-content-start align-items-start buttons-tooltip-bottom">
                                <SvgIcon size="medium" icon={LocationPinOutline} className="flex-shrink-0 secondary-text-color" />
                                <div className="w-100 d-flex">
                                    <span className="secondary-text-color w-25">Location:</span>
                                    {renderLastKnownLocationDetails(lastKnownLocation)}
                                </div>
                            </div>
                            <div className="d-flex gap-1 w-100 justify-content-start align-items-start buttons-tooltip-bottom">
                                <SvgIcon size="medium" icon={ClockOutline} className="flex-shrink-0 secondary-text-color" />
                                <div className="w-100 d-flex">
                                    <span className="secondary-text-color w-25">Time:</span>
                                    <span className="w-75">
                                        {lastKnownLocation.time ? DateTimeFormatter.formatDateTime(lastKnownLocation.time) : '-'}
                                    </span>
                                </div>
                            </div>
                            <div className="d-flex gap-1 w-100 align-items-start buttons-tooltip-bottom">
                                <SvgIcon size="medium" icon={AirportTowerOutline} className="flex-shrink-0 secondary-text-color" />
                                <div className="w-100 d-flex">
                                    <span className="secondary-text-color w-25">Origin:</span>
                                    <span className="w-75">{renderAirportLink(lastKnownLocation.origin)}</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {(fetchFailed || (!currentPosition && !lastKnownLocation && !isLoading)) && (
                    <div className="ps-3 d-flex align-items-center buttons-tooltip-bottom p-2">
                        Couldn't find location information for this aircraft.
                    </div>
                )}

                {isLoading && !currentPosition && !lastKnownLocation && (
                    <div className="d-flex justify-content-center align-items-center mt-1">
                        <LoadingIndicatorCircular />
                    </div>
                )}
            </div>
        </div>
    );
}
